import React, { useState, useMemo } from 'react';
import { Template } from '../../store/services/whatsappApi';

interface TemplateSelectorProps {
  templates: Template[];
  selectedTemplateId: string;
  onTemplateSelect: (templateId: string) => void;
  className?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplateId,
  onTemplateSelect,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Get unique categories from templates
  const categories = useMemo(() => {
    const cats = new Set(templates.map(t => t.category));
    return Array.from(cats).sort();
  }, [templates]);

  // Filter templates based on search term and category
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesSearch = !searchTerm ||
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [templates, searchTerm, selectedCategory]);

  // Get status color and text
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return { color: 'bg-green-100 text-green-800', text: 'Approved' };
      case 'PENDING':
        return { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' };
      case 'REJECTED':
        return { color: 'bg-red-100 text-red-800', text: 'Rejected' };
      case 'PAUSED':
        return { color: 'bg-gray-100 text-gray-800', text: 'Paused' };
      case 'DRAFT':
        return { color: 'bg-blue-100 text-blue-800', text: 'Draft' };
      default:
        return { color: 'bg-gray-100 text-gray-800', text: status };
    }
  };

  // Truncate content for preview
  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Check if template has media
  const hasMedia = (template: Template) => {
    return template.components?.some(component =>
      component.type === 'HEADER' && component.format && component.format !== 'TEXT'
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-whatsapp-green focus:border-whatsapp-green"
              placeholder="Search templates by name, content, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setSearchTerm('')}
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="sm:w-48">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-whatsapp-green focus:border-whatsapp-green"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Summary */}
      <div className="text-sm text-gray-600">
        {filteredTemplates.length === templates.length
          ? `Showing all ${templates.length} templates`
          : `Showing ${filteredTemplates.length} of ${templates.length} templates`
        }
        {(searchTerm || selectedCategory !== 'all') && (
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
            }}
            className="ml-2 text-whatsapp-green hover:text-whatsapp-dark-green underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
        {filteredTemplates.map((template) => {
          const statusInfo = getStatusInfo(template.status);
          const isSelected = selectedTemplateId === template.id.toString();

          return (
            <div
              key={template.id}
              className={`relative border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                isSelected
                  ? 'border-whatsapp-green bg-green-50 ring-2 ring-whatsapp-green ring-opacity-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              onClick={() => onTemplateSelect(template.id.toString())}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <svg className="h-6 w-6 text-whatsapp-green" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}

              {/* Template Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {template.name}
                  </h3>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    {template.language_code}
                  </p>
                </div>
              </div>

              {/* Status and Category */}
              <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                  {statusInfo.text}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {template.category}
                </span>
              </div>

              {/* Content Preview */}
              <div className="text-sm text-gray-700 mb-3">
                {truncateContent(template.content)}
              </div>

              {/* Template Features */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center space-x-2">
                  {hasMedia(template) && (
                    <span className="flex items-center">
                      <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Media
                    </span>
                  )}
                  {template.variables && template.variables.length > 0 && (
                    <span className="flex items-center">
                      <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" />
                        <path d="M9 8H11V16H9V8ZM13 8H15V16H13V8Z" />
                      </svg>
                      {template.variables.length} var{template.variables.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <span>
                  {new Date(template.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredTemplates.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.912-2.416" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedCategory !== 'all'
              ? 'Try adjusting your search terms or filters.'
              : 'No approved templates are available.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;